/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import {BoundingBox as RenderBoundingBox, EEmbededElementTypes, RenderDocument} from "js_renderer";
import Element from "../element";
import Factory from "../factory";

export default
class TexturedQuad extends Element {

    public static TypeName: string = EEmbededElementTypes.TEXTUREDQUAD;
    private _positions: number[];

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);

        // default values
        this.extendProperties({
            alwaysOnTop: true,
            width: 0.00001,
            height: 0.00001,
            leftOffset: 0,
            topOffset: 0
        });

        // fullscreen quad by default
        this._positions = [
            -1.0, -1.0, 0.0,
            1.0, -1.0, 0.0,
            1.0, 1.0, 0.0,
            -1.0, 1.0, 0.0
        ];
    }

    // abstract class
    public static create(aRenderDocument: RenderDocument): TexturedQuad {
        let element = new TexturedQuad(aRenderDocument);
        element._typeName = TexturedQuad.TypeName;
        return element;
    }

    /***
     * Set positions on screen in interval from 0 to 1 in both axis.
     *
     * @param aLeftOffset 0..1
     * @param aTopOffset 0..1
     * @param aWidth 0..1
     * @param aHeight 0..1
     */
    public setPositionAndSize(aLeftOffset: number, aTopOffset: number, aWidth: number, aHeight: number) {
        this._properties.width = aWidth;
        this._properties.height = aHeight;

        this._properties.leftOffset = aLeftOffset;
        this._properties.topOffset = aTopOffset;

        this._updatePositions();
    }

    public setTexture(aTextureUrl: string, aTextureSize: number[], aHasAlpha: boolean, aGlyphPos: number[], aGlyphSize: number[]) {
        this.extendProperties({
            textureUrl: aTextureUrl,
            textureSize: aTextureSize,
            textureHasAlpha: aHasAlpha,
            glyphPosition: aGlyphPos,
            glyphSize: aGlyphSize
        });
    }

    public getTypeName(): string {
        return TexturedQuad.TypeName;
    }

    // TODO: @ Jarek: the model element can contain more than one poi part, so we should generate more poi geometry for such model element
    protected _generateElementDescriptionJSON(): any {
        // these are model element properties taken from the scene api element properties
        return {
            type: this._properties.type,
            id: this.getTypeName() + "_" + this._UID,   // this is is not important in the engine, it can identify e.g. id of an element in Lexocad
            UID: this._UID
        };
    }

    protected _generateGeometryObjectDescriptionJSON(): any {
        let geometry = this._createGeometry();

        return [{
            description: {
                type: "HUDSHAPE",
                color: [0.0, 0.0, 0.0],
                bboxMin: [geometry.bbox.min[0], geometry.bbox.min[1], geometry.bbox.min[2]],
                bboxMax: [geometry.bbox.max[0], geometry.bbox.max[1], geometry.bbox.max[2]]
            },
            appearance: {
                renderOption: {
                    alwaysOnTop: this._properties.alwaysOnTop,
                    pickable: this._properties.pickable
                },
                imageTexture: {
                    url: [this._properties.textureUrl],
                    width: [0],
                    height: [0],
                    offsets: [0],
                    sizes: [0],
                    tiled: [false],
                    alpha: true,
                    model: "BLEND",
                    blendColor: [1, 1, 1],
                    repeatS: 0,
                    repeatT: 0
                }
            },
            indexedFaceSet: {
                src: this._renderDocument.getRenderer()._dataStorage.createLocalResourceUri(),
                vertexCount: geometry.vertexCount,
                normalCount: geometry.normalCount,
                texcoordCount: geometry.texcoordCount,
                indexCount: geometry.indexCount,
                binSize: 0,
                positions: this._positions,
                normals: geometry.normals,
                texCoords: geometry.texCoords,
                indices: geometry.indices
            }
        }];
    }

    private _createGeometry(): any {
        let geometry = {
            vertexCount: 0,
            normalCount: 0,
            texcoordCount: 0,
            indexCount: 0,
            vertices: this._positions,
            normals: [],
            texCoords: [],
            indices: [],
            bbox: new RenderBoundingBox()
        };

        // we need 4 points
        // left bottom point
        geometry.bbox.extendByXYZ(this._positions[0], this._positions[1], this._positions[2]);
        geometry.normals = [0.0, 0.0, 1.0];
        geometry.texCoords = [this._properties.glyphPosition[0], this._properties.glyphPosition[1]];

        // right bottom point
        geometry.bbox.extendByXYZ(this._positions[3], this._positions[4], this._positions[5]);
        geometry.normals = geometry.normals.concat([0.0, 0.0, 1.0]);
        geometry.texCoords = geometry.texCoords.concat([this._properties.glyphPosition[0] + this._properties.glyphSize[0], this._properties.glyphPosition[1]]);

        // right top point
        geometry.bbox.extendByXYZ(this._positions[6], this._positions[7], this._positions[8]);
        geometry.normals = geometry.normals.concat([0.0, 0.0, 1.0]);
        geometry.texCoords = geometry.texCoords.concat([this._properties.glyphPosition[0] + this._properties.glyphSize[0], this._properties.glyphPosition[1] + this._properties.glyphSize[1]]);

        // left top point
        geometry.bbox.extendByXYZ(this._positions[9], this._positions[10], this._positions[11]);
        geometry.normals = geometry.normals.concat([0.0, 0.0, 1.0]);
        geometry.texCoords = geometry.texCoords.concat([this._properties.glyphPosition[0], this._properties.glyphPosition[1] + this._properties.glyphSize[1]]);

        // indices
        geometry.indices = [0, 1, 2, 0, 2, 3];
        geometry.vertexCount = this._positions.length / 3;
        geometry.normalCount = geometry.normals.length / 3;
        geometry.texcoordCount = geometry.texCoords.length / 2;
        geometry.indexCount = geometry.indices.length;

        return geometry;
    }

    private _updatePositions() {
        // we need 4 points
        // left bottom point
        this._positions = [
            -1.0 + 2 * this._properties.leftOffset,
            -1.0,
            0.0
        ];

        // right bottom point
        this._positions = this._positions.concat([
            -1.0 + 2 * (this._properties.leftOffset + this._properties.width),
            -1.0,
            0.0
        ]);

        // right top point
        this._positions = this._positions.concat([
            -1.0 + 2 * (this._properties.leftOffset + this._properties.width),
            -1.0 + 2 * (this._properties.topOffset + this._properties.height),
            0.0
        ]);

        // left top point
        this._positions = this._positions.concat([
            -1.0 + 2 * this._properties.leftOffset,
            -1.0 + 2 * (this._properties.topOffset + this._properties.height),
            0.0
        ]);

        this.updatePositions([this._positions]);
    }
}

Factory.register(TexturedQuad);
